import type { Database } from "@pex/database/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  BillingApp,
  BillingProcessor,
  BillingSubscription,
  SubscriptionStatus,
} from "../types";

function sharedSchema(client: SupabaseClient<Database>) {
  // biome-ignore lint/suspicious/noExplicitAny: schema() is not typed in the generated Database type
  return (client as unknown as any).schema("shared");
}

function subs(client: SupabaseClient<Database>) {
  return sharedSchema(client).from("subscriptions");
}

function rowToSubscription(row: Record<string, unknown>): BillingSubscription {
  return {
    id: row.id as string,
    organization_id: (row.organization_id as string | null) ?? null,
    setup_account_id: (row.setup_account_id as string | null) ?? null,
    app: row.app as BillingApp,
    plan_id: (row.plan_id as string | null) ?? null,
    processor: row.processor as BillingProcessor,
    processor_subscription_id:
      (row.processor_subscription_id as string | null) ?? null,
    processor_metadata:
      (row.processor_metadata as Record<string, unknown>) ?? {},
    plan_type: row.plan_type as BillingSubscription["plan_type"],
    status: row.status as SubscriptionStatus,
    status_raw: (row.status_raw as string | null) ?? null,
    limits: (row.limits as Record<string, number | null>) ?? {},
    currency: row.currency as string,
    retry_count: row.retry_count as number,
    next_billing_date: (row.next_billing_date as string | null) ?? null,
    created_at: row.created_at as string,
    updated_at: row.updated_at as string,
  };
}

export async function getOrgSubscription(
  client: SupabaseClient<Database>,
  organizationId: string,
  app: BillingApp
): Promise<BillingSubscription | null> {
  const { data, error } = await subs(client)
    .select("*")
    .eq("organization_id", organizationId)
    .eq("app", app)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch subscription: ${error.message}`);
  }

  return data ? rowToSubscription(data) : null;
}

export async function getOrgSubscriptions(
  client: SupabaseClient<Database>,
  organizationId: string
): Promise<BillingSubscription[]> {
  const { data, error } = await subs(client)
    .select("*")
    .eq("organization_id", organizationId);

  if (error) {
    throw new Error(`Failed to fetch subscriptions: ${error.message}`);
  }

  return (data ?? []).map(rowToSubscription);
}

export async function getPendingSubscription(
  client: SupabaseClient<Database>,
  setupAccountId: string,
  app: BillingApp
): Promise<BillingSubscription | null> {
  const { data, error } = await subs(client)
    .select("*")
    .is("organization_id", null)
    .eq("setup_account_id", setupAccountId)
    .eq("app", app)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch pending subscription: ${error.message}`);
  }

  return data ? rowToSubscription(data) : null;
}

export async function updateSubscriptionFromWebhook(
  client: SupabaseClient<Database>,
  processorSubscriptionId: string,
  processor: BillingProcessor,
  updates: Partial<{
    status: SubscriptionStatus;
    status_raw: string;
    next_billing_date: string | null;
    processor_metadata: Record<string, unknown>;
    limits: Record<string, number | null>;
    plan_id: string | null;
  }>
): Promise<BillingSubscription | null> {
  const { data, error } = await subs(client)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("processor_subscription_id", processorSubscriptionId)
    .eq("processor", processor)
    .select()
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to update subscription from webhook: ${error.message}`
    );
  }

  return data ? rowToSubscription(data) : null;
}

export async function activatePendingSubscription(
  client: SupabaseClient<Database>,
  subscriptionId: string,
  organizationId: string
): Promise<void> {
  // shared.subscriptions denies direct authenticated writes (GH #597). The SECURITY DEFINER
  // RPC validates the caller owns the pending row (setup_account_id) and facilitates the target
  // org, then links it. Service-role callers (webhook) bypass the scope check inside the RPC.
  const { error } = await sharedSchema(client).rpc(
    "activate_pending_subscription",
    { p_id: subscriptionId, p_org: organizationId }
  );

  if (error) {
    throw new Error(
      `Failed to activate pending subscription: ${error.message}`
    );
  }
  // payment_events.organization_id is backfilled automatically by the
  // subscriptions_backfill_payment_events_org_id DB trigger (migration
  // 20260310000044). No application-layer update needed here.
}

export async function incrementRetryCount(
  client: SupabaseClient<Database>,
  subscriptionId: string
): Promise<void> {
  const { error } = await sharedSchema(client).rpc(
    "increment_subscription_retry_count",
    { sub_id: subscriptionId }
  );

  if (error) {
    // Fallback: manual increment via select + update
    const { data: current, error: fetchErr } = await subs(client)
      .select("retry_count")
      .eq("id", subscriptionId)
      .single();

    if (fetchErr) {
      throw new Error(`Failed to fetch retry count: ${fetchErr.message}`);
    }

    const { error: updateErr } = await subs(client)
      .update({
        retry_count:
          ((current as { retry_count: number }).retry_count ?? 0) + 1,
        updated_at: new Date().toISOString(),
      })
      .eq("id", subscriptionId);

    if (updateErr) {
      throw new Error(`Failed to increment retry count: ${updateErr.message}`);
    }
  }
}

export interface CreatePendingSubscriptionInput {
  app: BillingApp;
  currency: string;
  limits: Record<string, number | null>;
  nextBillingDate: string | null;
  planId: string | null;
  planType: BillingSubscription["plan_type"];
  processor: BillingProcessor;
  processorMetadata: Record<string, unknown>;
  processorSubscriptionId: string;
  setupAccountId: string;
  status: SubscriptionStatus;
  statusRaw: string;
}

export async function createOrUpdatePendingSubscription(
  client: SupabaseClient<Database>,
  input: CreatePendingSubscriptionInput
): Promise<BillingSubscription> {
  const existing = await getPendingSubscription(
    client,
    input.setupAccountId,
    input.app
  );

  if (existing) {
    // Block if a checkout is actively in progress (has a Paystack subscription ID
    // and was created within the last 24 hours). This prevents a second browser
    // tab or retry from silently overwriting a live checkout session.
    const ageHours =
      (Date.now() - new Date(existing.created_at).getTime()) / (1000 * 60 * 60);
    const isStale = ageHours > 24;

    if (!isStale && existing.processor_subscription_id) {
      throw new Error(
        "PENDING_SUBSCRIPTION_IN_PROGRESS: An org setup is already in progress. Complete it at /create-org/complete."
      );
    }

    // Update the existing pending subscription — handles repeat checkout or stale sessions.
    // shared.subscriptions denies direct authenticated writes (GH #597); the self-scoped RPC
    // validates setup_account_id = auth.uid() (bypassed for service-role) then updates.
    const { data, error } = await sharedSchema(client).rpc(
      "update_pending_subscription_row",
      {
        p_id: existing.id,
        p_plan_id: input.planId ?? undefined,
        p_processor_subscription_id: input.processorSubscriptionId,
        p_processor_metadata: input.processorMetadata,
        p_plan_type: input.planType,
        p_status: input.status,
        p_status_raw: input.statusRaw,
        p_limits: input.limits,
        p_currency: input.currency,
        p_next_billing_date: input.nextBillingDate ?? undefined,
      }
    );

    if (error || !data) {
      throw new Error(
        `Failed to update pending subscription: ${error?.message ?? "no row"}`
      );
    }

    return rowToSubscription(data);
  }

  // Insert a new pending subscription via the self-scoped RPC (org null, setup = caller).
  const { data, error } = await sharedSchema(client).rpc(
    "create_pending_subscription_row",
    {
      p_setup_account_id: input.setupAccountId,
      p_app: input.app,
      p_plan_id: input.planId ?? undefined,
      p_processor: input.processor,
      p_processor_subscription_id: input.processorSubscriptionId,
      p_processor_metadata: input.processorMetadata,
      p_plan_type: input.planType,
      p_status: input.status,
      p_status_raw: input.statusRaw,
      p_limits: input.limits,
      p_currency: input.currency,
      p_next_billing_date: input.nextBillingDate ?? undefined,
    }
  );

  if (error || !data) {
    throw new Error(
      `Failed to create pending subscription: ${error?.message ?? "no row"}`
    );
  }

  return rowToSubscription(data);
}

export async function getSubscriptionByProcessorId(
  client: SupabaseClient<Database>,
  processorSubscriptionId: string,
  processor: BillingProcessor
): Promise<BillingSubscription | null> {
  const { data, error } = await subs(client)
    .select("*")
    .eq("processor_subscription_id", processorSubscriptionId)
    .eq("processor", processor)
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to fetch subscription by processor ID: ${error.message}`
    );
  }

  return data ? rowToSubscription(data) : null;
}

export async function resetRetryCount(
  client: SupabaseClient<Database>,
  subscriptionId: string
): Promise<void> {
  const { error } = await subs(client)
    .update({ retry_count: 0, updated_at: new Date().toISOString() })
    .eq("id", subscriptionId);

  if (error) {
    throw new Error(`Failed to reset retry count: ${error.message}`);
  }
}

export async function getActiveSubscriptionByAccount(
  client: SupabaseClient<Database>,
  setupAccountId: string,
  app: BillingApp
): Promise<BillingSubscription | null> {
  const { data, error } = await subs(client)
    .select("*")
    .eq("setup_account_id", setupAccountId)
    .eq("app", app)
    .not("organization_id", "is", null)
    .in("status", ["active", "non_renewing"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to fetch active subscription by account: ${error.message}`
    );
  }

  return data ? rowToSubscription(data) : null;
}

export async function updateSubscriptionById(
  client: SupabaseClient<Database>,
  subscriptionId: string,
  updates: Partial<{
    limits: Record<string, number | null>;
    next_billing_date: string | null;
    plan_id: string | null;
    processor_subscription_id: string;
    status: SubscriptionStatus;
    status_raw: string;
  }>
): Promise<void> {
  const { error } = await subs(client)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", subscriptionId);

  if (error) {
    throw new Error(`Failed to update subscription: ${error.message}`);
  }
}

export interface InsertSubscriptionChangeInput {
  changedBy: string | null;
  newLimits: Record<string, number | null>;
  newPlanId: string;
  previousLimits: Record<string, number | null>;
  previousPlanId: string | null;
  reason?: string;
  source: "admin" | "processor" | "customer";
  subscriptionId: string;
}

export async function insertSubscriptionChange(
  client: SupabaseClient<Database>,
  input: InsertSubscriptionChangeInput
): Promise<void> {
  // shared.subscription_changes denies direct authenticated inserts (GH #597). The
  // SECURITY DEFINER RPC validates changed_by = auth.uid() and that the caller is an
  // active facilitator of the subscription's org, then inserts the audit row. The sole
  // runtime caller is the authenticated Bridge upgrade/downgrade flow.
  const { error } = await sharedSchema(client).rpc(
    "insert_subscription_change_scoped",
    {
      p_subscription_id: input.subscriptionId,
      p_changed_by: input.changedBy,
      p_previous_plan_id: input.previousPlanId,
      p_new_plan_id: input.newPlanId,
      p_previous_limits: input.previousLimits,
      p_new_limits: input.newLimits,
      p_reason: input.reason ?? null,
      p_source: input.source,
    }
  );

  if (error) {
    throw new Error(`Failed to insert subscription change: ${error.message}`);
  }
}

// Atomically sets pending_plan_change = true for the given subscription, but
// only if it is currently false. Returns true if the claim succeeded (this
// caller owns the lock), false if another in-flight change already holds it.
export async function claimPlanChange(
  client: SupabaseClient<Database>,
  subscriptionId: string
): Promise<boolean> {
  // shared.subscriptions denies direct authenticated writes (GH #597). The SECURITY DEFINER
  // RPC validates facilitator scope (bypassed for service-role) and performs the conditional
  // claim, returning true only if this caller won the lock.
  const { data } = await sharedSchema(client).rpc("claim_plan_change", {
    p_id: subscriptionId,
  });
  return data === true;
}

// Releases the pending_plan_change claim regardless of outcome. Always called
// in a finally block after the Paystack round-trip completes or errors.
export async function releasePlanChange(
  client: SupabaseClient<Database>,
  subscriptionId: string
): Promise<void> {
  await sharedSchema(client).rpc("release_plan_change", {
    p_id: subscriptionId,
  });
}
